import { useEffect } from 'react';

/**
 * Keep `document.title` in step with the route (M13.7, WCAG 2.4.2 Page Titled, Level A).
 *
 * All three apps are single-page apps served from one `index.html`, so every screen
 * inherited one static title — "HeyAllergy Clinic Admin" whether you were on the
 * dashboard or on a patient record. 2.4.2 asks for a title that "describes topic or
 * purpose", and in a SPA the only way to have one is to write it on navigation. It is
 * also the label in the browser's tab strip, its history and its back-button menu, all of
 * which were unusable with sixteen identical entries.
 *
 * `document` is touched in an effect rather than during render because it is a write to
 * something outside React, and the title is restored on unmount only if this hook was the
 * last to set it — so a screen that unmounts during a transition cannot undo the title the
 * next screen has already written.
 */
export function useDocumentTitle(title: string): void {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const previous = document.title;
    document.title = title;
    return () => {
      if (document.title === title) document.title = previous;
    };
  }, [title]);
}
